import React, { createContext, useContext, useEffect, useState } from 'react';
import type { TabContextType, TabProviderProps } from './Tabs.types';

const tabsContextInitialValue: TabContextType = {
  style: 'Filled',
  currentTab: '',
  updateCurrentTab: () => { },
};

export const TabContext = createContext<TabContextType>(tabsContextInitialValue);

/**
 * Custom hook to use the TabContext
 * Throws an error if used outside of a TabProvider
 */
export const useTab = () => {
  const context = useContext(TabContext);
  if (!context) {
    throw new Error('useTab must be used within a TabProvider');
  }
  return context;
};

/**
 * Provides context for Tab components
 * Manages the current tab state and providing a method to update
 */
export const TabProvider = ({
  children,
  value,
  style = 'Filled',
  onClick,
}: TabProviderProps) => {
  const [internalCurrentTab, setInternalCurrentTab] = useState<string | number>('');

  const updateCurrentTab = (tabValue: string | number) => {
    if (internalCurrentTab !== tabValue) {
      if (value && onClick) {
        onClick(tabValue);
      }
      setInternalCurrentTab(tabValue);
    }
  };

  useEffect(() => {
    if (value !== undefined && onClick) {
      setInternalCurrentTab(value);
    }
  }, [value]);

  return (
    <TabContext.Provider value={{ style, currentTab: internalCurrentTab, updateCurrentTab }}>
      {children}
    </TabContext.Provider>
  );
};